import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Pencil, X, Eye, EyeOff, Quote } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMPTY = { quote: "", author: "", role: "", order: 0, published: true };

export default function TestimonialsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/testimonials/all").then((r) => setItems(r.data.testimonials)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.quote.trim() || !form.author.trim()) { toast.error("Quote and author are required."); return; }
    setBusy(true);
    try {
      await api.post("/testimonials", { ...form, order: Number(form.order) || 0 });
      toast.success("Testimonial added."); setForm(EMPTY); setAdding(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const startEdit = (t) => { setEditId(t.id); setEditForm({ quote: t.quote, author: t.author, role: t.role || "", order: t.order || 0, published: t.published !== false }); };
  const saveEdit = async () => {
    if (!editForm.quote.trim() || !editForm.author.trim()) { toast.error("Quote and author are required."); return; }
    setBusy(true);
    try {
      await api.put(`/testimonials/${editId}`, { ...editForm, order: Number(editForm.order) || 0 });
      toast.success("Testimonial updated."); setEditId(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const togglePublish = async (t) => {
    try {
      await api.put(`/testimonials/${t.id}`, { quote: t.quote, author: t.author, role: t.role || "", order: t.order || 0, published: !(t.published !== false) });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    setDelId(id);
    try { await api.delete(`/testimonials/${id}`); toast.success("Testimonial deleted."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDelId(null); }
  };

  const Input = ({ label, value, onChange, testid, area }) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">{label}</label>
      {area ? (
        <textarea data-testid={testid} rows={4} value={value} onChange={(e) => onChange(e.target.value)}
          className="border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none resize-y leading-relaxed" />
      ) : (
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
          className="border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
      )}
    </div>
  );

  return (
    <div data-testid="testimonials-panel">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight text-[#0A0D0B]">Testimonials</h1>
          <p className="mt-1 text-sm text-[#58635C]">Client quotes shown on your public home page. Add, edit, hide or remove them here.</p>
        </div>
        {!adding && (
          <button onClick={() => { setForm(EMPTY); setAdding(true); }} data-testid="testimonial-add-open"
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#0A0D0B] text-white px-4 py-2.5 hover:bg-[#233027] transition-colors">
            <Plus size={15} /> Add testimonial
          </button>
        )}
      </div>

      {adding && (
        <div className="border border-[#E5E1D8] bg-white p-5 mb-6" data-testid="testimonial-add-form">
          <div className="grid gap-4">
            <Input label="Quote" area value={form.quote} onChange={(v) => setForm((f) => ({ ...f, quote: v }))} testid="testimonial-quote" />
            <div className="grid sm:grid-cols-3 gap-4">
              <Input label="Author / business" value={form.author} onChange={(v) => setForm((f) => ({ ...f, author: v }))} testid="testimonial-author" />
              <Input label="Role / location" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} testid="testimonial-role" />
              <Input label="Order (0 = first)" value={form.order} onChange={(v) => setForm((f) => ({ ...f, order: v }))} testid="testimonial-order" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setAdding(false)} data-testid="testimonial-cancel"
              className="inline-flex items-center gap-1.5 text-sm font-semibold border border-[#E5E1D8] text-[#58635C] px-4 py-2 hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} disabled={busy} data-testid="testimonial-save"
              className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#0A0D0B] text-white px-4 py-2 hover:bg-[#233027] transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>
      )}

      {!items ? (
        <Loader2 className="animate-spin text-[#B89874]" />
      ) : items.length === 0 ? (
        <p className="text-sm text-[#58635C]">No testimonials yet. Add your first one above.</p>
      ) : (
        <div className="space-y-4">
          {items.map((t) => (
            editId === t.id ? (
              <div key={t.id} className="border border-[#B89874] bg-white p-5" data-testid={`testimonial-edit-${t.id}`}>
                <div className="grid gap-4">
                  <Input label="Quote" area value={editForm.quote} onChange={(v) => setEditForm((f) => ({ ...f, quote: v }))} testid={`testimonial-edit-quote-${t.id}`} />
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Input label="Author / business" value={editForm.author} onChange={(v) => setEditForm((f) => ({ ...f, author: v }))} testid={`testimonial-edit-author-${t.id}`} />
                    <Input label="Role / location" value={editForm.role} onChange={(v) => setEditForm((f) => ({ ...f, role: v }))} testid={`testimonial-edit-role-${t.id}`} />
                    <Input label="Order (0 = first)" value={editForm.order} onChange={(v) => setEditForm((f) => ({ ...f, order: v }))} testid={`testimonial-edit-order-${t.id}`} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button onClick={() => setEditId(null)} data-testid={`testimonial-edit-cancel-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold border border-[#E5E1D8] text-[#58635C] px-4 py-2 hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                    <X size={14} /> Cancel
                  </button>
                  <button onClick={saveEdit} disabled={busy} data-testid={`testimonial-edit-save-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#0A0D0B] text-white px-4 py-2 hover:bg-[#233027] transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                  </button>
                </div>
              </div>
            ) : (
              <div key={t.id} className={`border p-5 flex gap-4 ${t.published === false ? "border-[#E5E1D8] bg-[#FBFAF5] opacity-70" : "border-[#E5E1D8] bg-white"}`} data-testid={`testimonial-row-${t.id}`}>
                <Quote size={22} className="text-[#B89874] shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#233027] leading-relaxed">“{t.quote}”</p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#0A0D0B]">{t.author}</span>
                    {t.role && <span className="text-xs text-[#B89874] uppercase tracking-[0.12em]">· {t.role}</span>}
                    <span className="text-[11px] text-[#58635C] font-mono-gg ml-1">#{t.order ?? 0}</span>
                    {t.published === false && <span className="text-[10px] uppercase tracking-wide bg-[#F0EDE4] text-[#58635C] px-2 py-0.5">Hidden</span>}
                  </div>
                </div>
                <div className="flex items-start gap-1.5 shrink-0">
                  <button onClick={() => togglePublish(t)} data-testid={`testimonial-toggle-${t.id}`} title={t.published === false ? "Show on site" : "Hide from site"}
                    className="p-2 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors">
                    {t.published === false ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => startEdit(t)} data-testid={`testimonial-edit-open-${t.id}`} title="Edit"
                    className="p-2 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(t.id)} disabled={delId === t.id} data-testid={`testimonial-delete-${t.id}`} title="Delete"
                    className="p-2 border border-[#E5E1D8] text-[#58635C] hover:text-[#B0403A] hover:border-[#B0403A] transition-colors disabled:opacity-50">
                    {delId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
